// Share Certificate PDF generator
// Renders a beautiful HTML certificate, snapshots it via html2canvas,
// and downloads as a PDF via jsPDF.
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const fmtDate = (d) => new Date(d || Date.now()).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const shortId = (id = '') => id.replace(/-/g, '').slice(0, 7).toUpperCase();

export const buildCertificateNumber = (purchaseId) => `SGW-CERT-${shortId(purchaseId)}`;
export const buildRegistryRef = (purchaseId) => `SGW-REG-${shortId(purchaseId)}-D`;

export const CertificateTemplate = ({ purchase, holderName, country }) => {
  const certNo = buildCertificateNumber(purchase.id);
  const regRef = buildRegistryRef(purchase.id);
  const date = fmtDate(purchase.processed_at || purchase.created_at);
  return (
    <div
      id={`cert-${purchase.id}`}
      data-testid={`cert-template-${purchase.id}`}
      style={{
        width: '1000px',
        height: '700px',
        background: '#fdf9e7',
        padding: '40px',
        fontFamily: '"Playfair Display", Georgia, serif',
        color: '#0f2a1a',
        boxSizing: 'border-box',
        position: 'relative',
        border: '8px double #1d5e3a',
        borderRadius: '8px',
      }}
    >
      {/* Ornate inner border */}
      <div style={{
        position: 'absolute', inset: '18px',
        border: '1.5px solid rgba(29,94,58,0.5)',
        borderRadius: '4px',
        pointerEvents: 'none',
      }} />
      {/* Corner flourishes */}
      {['top left','top right','bottom left','bottom right'].map((pos) => {
        const [v, h] = pos.split(' ');
        return (
          <div key={pos} style={{
            position: 'absolute',
            [v]: '26px',
            [h]: '26px',
            width: '30px', height: '30px',
            border: '2px solid #b98a2b',
            [`border${v[0].toUpperCase() + v.slice(1)}LeftRadius`]: 0,
          }} />
        );
      })}

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px 20px' }}>
        <div style={{ border: '1.5px dashed #1d5e3a', padding: '6px 14px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace', color: '#1d5e3a', letterSpacing: '1px' }}>
          <div style={{ fontSize: '9px', color: '#6b7f70', letterSpacing: '2px', marginBottom: '3px', fontFamily: 'sans-serif' }}>NO. OF CERTIFICATE</div>
          {certNo}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '28px', color: '#1d5e3a', fontWeight: 700, letterSpacing: '1px' }}>
            SkyWay <span style={{ color: '#b98a2b' }}>Green</span>
          </div>
          <div style={{ fontSize: '10px', letterSpacing: '4px', color: '#6b7f70', marginTop: '2px', fontFamily: 'sans-serif' }}>ECOSYSTEM INFRASTRUCTURE</div>
        </div>
        <div style={{ border: '1.5px dashed #1d5e3a', padding: '6px 14px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace', color: '#1d5e3a', textAlign: 'right' }}>
          <div style={{ fontSize: '9px', color: '#6b7f70', letterSpacing: '2px', marginBottom: '3px', fontFamily: 'sans-serif' }}>NO. OF SHARE UNITS</div>
          {fmtNum(purchase.package_shares)} Shares
        </div>
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <div style={{ fontSize: '46px', fontWeight: 700, color: '#0f2a1a', letterSpacing: '6px' }}>SHARE CERTIFICATE</div>
        <div style={{
          margin: '10px auto 0',
          width: '260px', height: '2px',
          background: 'linear-gradient(90deg, transparent, #b98a2b, transparent)',
        }} />
        <div style={{ marginTop: '14px', fontSize: '11px', letterSpacing: '4px', color: '#6b7f70', fontFamily: 'sans-serif' }}>
          CLASS A CO-OWNERSHIP EQUITY SHARES
        </div>
      </div>

      {/* Body */}
      <div style={{ textAlign: 'center', marginTop: '40px', padding: '0 60px' }}>
        <div style={{ fontStyle: 'italic', color: '#3a4b3f', fontSize: '15px' }}>This is to certify that the registered co-owner:</div>
        <div style={{ marginTop: '18px', fontSize: '32px', fontWeight: 700, color: '#0f2a1a', letterSpacing: '2px' }}>
          {(holderName || '').toUpperCase()}
        </div>
        <div style={{ marginTop: '6px', height: '1px', background: '#b98a2b', width: '60%', marginInline: 'auto', opacity: 0.5 }} />
        <div style={{ marginTop: '6px', fontSize: '10px', letterSpacing: '3px', color: '#6b7f70', fontFamily: 'sans-serif' }}>
          HOLDER NAME &nbsp;·&nbsp; COUNTRY OF RESIDENCE: {(country || '—').toUpperCase()}
        </div>

        <div style={{ marginTop: '26px', fontSize: '15px', lineHeight: '1.8', color: '#1a2b1f', fontStyle: 'italic' }}>
          is the lawful registered holder of{' '}
          <span style={{
            display: 'inline-block', padding: '2px 12px',
            border: '1.5px dashed #b98a2b', borderRadius: '4px',
            fontStyle: 'normal', fontWeight: 700, color: '#1d5e3a',
          }}>
            {fmtNum(purchase.package_shares)}
          </span>{' '}
          fully-paid and non-assessable Class&nbsp;A Equity Shares in the{' '}
          <span style={{ fontWeight: 700, fontStyle: 'normal' }}>SkyWay Project Segment</span>{' '}
          of the SkyWay Green Technology Network, acquired for a total co-ownership allocation of{' '}
          <span style={{ fontWeight: 700, fontStyle: 'normal', color: '#b98a2b' }}>${fmtNum(purchase.package_price)}.00</span>.
        </div>

        <div style={{ marginTop: '22px', fontSize: '11px', color: '#6b7f70', lineHeight: '1.6', fontStyle: 'italic', fontFamily: 'sans-serif' }}>
          These shares represent partial co-ownership of the specific SkyWay technology segment and are fully convertible and
          registered under security reference ledger number{' '}
          <span style={{ fontFamily: 'monospace', color: '#0f2a1a', fontStyle: 'normal' }}>{regRef}</span>.
        </div>
      </div>

      {/* Bottom row: date | seal | signature */}
      <div style={{ position: 'absolute', bottom: '50px', left: '60px', right: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#6b7f70', fontFamily: 'sans-serif' }}>DATE OF REGISTRATION</div>
          <div style={{ marginTop: '6px', fontSize: '16px', color: '#0f2a1a' }}>{date}</div>
        </div>

        {/* Common Seal */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '90px', height: '90px', borderRadius: '50%',
            border: '3px solid #b98a2b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column',
            boxShadow: '0 0 0 3px #fdf9e7, 0 0 0 4px #b98a2b',
            transform: 'rotate(-8deg)',
          }}>
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#b98a2b', letterSpacing: '1.5px', fontFamily: 'sans-serif' }}>SKYWAY GREEN</div>
            <div style={{ fontSize: '18px', color: '#b98a2b', margin: '2px 0' }}>★</div>
            <div style={{ fontSize: '7px', fontWeight: 700, color: '#b98a2b', letterSpacing: '2px', fontFamily: 'sans-serif' }}>COMMON SEAL</div>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#6b7f70', fontFamily: 'sans-serif' }}>AUTHORIZED ATTORNEY</div>
          <div style={{ marginTop: '6px', fontSize: '20px', color: '#0f2a1a', fontStyle: 'italic', fontFamily: '"Great Vibes", cursive' }}>
            Evgenii Kudriashov
          </div>
          <div style={{ marginTop: '4px', height: '1px', background: '#0f2a1a', width: '160px', marginLeft: 'auto', opacity: 0.6 }} />
          <div style={{ marginTop: '4px', fontSize: '9px', letterSpacing: '3px', color: '#6b7f70', fontFamily: 'sans-serif' }}>SGW LEGAL REPRESENTATIVE</div>
        </div>
      </div>
    </div>
  );
};

export const downloadCertificatePdf = async (purchase, holderName, country) => {
  // Create hidden container
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.left = '-99999px';
  wrapper.style.top = '0';
  document.body.appendChild(wrapper);

  // Render certificate template into DOM via ReactDOM
  // We use a lightweight approach: inject raw HTML equivalent
  const certNo = buildCertificateNumber(purchase.id);
  const regRef = buildRegistryRef(purchase.id);
  const date = fmtDate(purchase.processed_at || purchase.created_at);
  const holder = (holderName || '').toUpperCase();
  const shares = fmtNum(purchase.package_shares);
  const price = fmtNum(purchase.package_price);
  const countryUp = (country || '—').toUpperCase();

  wrapper.innerHTML = `
  <div id="cert-render" style="width:1000px;height:700px;background:#fdf9e7;padding:40px;font-family:'Playfair Display',Georgia,serif;color:#0f2a1a;box-sizing:border-box;position:relative;border:8px double #1d5e3a;border-radius:8px;">
    <div style="position:absolute;inset:18px;border:1.5px solid rgba(29,94,58,0.5);border-radius:4px;pointer-events:none;"></div>

    <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 20px 20px;">
      <div style="border:1.5px dashed #1d5e3a;padding:6px 14px;border-radius:4px;font-size:13px;font-family:monospace;color:#1d5e3a;letter-spacing:1px;">
        <div style="font-size:9px;color:#6b7f70;letter-spacing:2px;margin-bottom:3px;font-family:sans-serif;">NO. OF CERTIFICATE</div>
        ${certNo}
      </div>
      <div style="text-align:center;">
        <div style="font-size:28px;color:#1d5e3a;font-weight:700;letter-spacing:1px;">SkyWay <span style="color:#b98a2b;">Green</span></div>
        <div style="font-size:10px;letter-spacing:4px;color:#6b7f70;margin-top:2px;font-family:sans-serif;">ECOSYSTEM INFRASTRUCTURE</div>
      </div>
      <div style="border:1.5px dashed #1d5e3a;padding:6px 14px;border-radius:4px;font-size:13px;font-family:monospace;color:#1d5e3a;text-align:right;">
        <div style="font-size:9px;color:#6b7f70;letter-spacing:2px;margin-bottom:3px;font-family:sans-serif;">NO. OF SHARE UNITS</div>
        ${shares} Shares
      </div>
    </div>

    <div style="text-align:center;margin-top:30px;">
      <div style="font-size:46px;font-weight:700;color:#0f2a1a;letter-spacing:6px;">SHARE CERTIFICATE</div>
      <div style="margin:10px auto 0;width:260px;height:2px;background:linear-gradient(90deg,transparent,#b98a2b,transparent);"></div>
      <div style="margin-top:14px;font-size:11px;letter-spacing:4px;color:#6b7f70;font-family:sans-serif;">CLASS A CO-OWNERSHIP EQUITY SHARES</div>
    </div>

    <div style="text-align:center;margin-top:40px;padding:0 60px;">
      <div style="font-style:italic;color:#3a4b3f;font-size:15px;">This is to certify that the registered co-owner:</div>
      <div style="margin-top:18px;font-size:32px;font-weight:700;color:#0f2a1a;letter-spacing:2px;">${holder}</div>
      <div style="margin-top:6px;height:1px;background:#b98a2b;width:60%;margin:6px auto 0;opacity:0.5;"></div>
      <div style="margin-top:6px;font-size:10px;letter-spacing:3px;color:#6b7f70;font-family:sans-serif;">HOLDER NAME &nbsp;·&nbsp; COUNTRY OF RESIDENCE: ${countryUp}</div>
      <div style="margin-top:26px;font-size:15px;line-height:1.8;color:#1a2b1f;font-style:italic;">
        is the lawful registered holder of
        <span style="display:inline-block;padding:2px 12px;border:1.5px dashed #b98a2b;border-radius:4px;font-style:normal;font-weight:700;color:#1d5e3a;">${shares}</span>
        fully-paid and non-assessable Class&nbsp;A Equity Shares in the
        <span style="font-weight:700;font-style:normal;">SkyWay Project Segment</span>
        of the SkyWay Green Technology Network, acquired for a total co-ownership allocation of
        <span style="font-weight:700;font-style:normal;color:#b98a2b;">$${price}.00</span>.
      </div>
      <div style="margin-top:22px;font-size:11px;color:#6b7f70;line-height:1.6;font-style:italic;font-family:sans-serif;">
        These shares represent partial co-ownership of the specific SkyWay technology segment and are fully convertible and registered under security reference ledger number
        <span style="font-family:monospace;color:#0f2a1a;font-style:normal;">${regRef}</span>.
      </div>
    </div>

    <div style="position:absolute;bottom:50px;left:60px;right:60px;display:flex;justify-content:space-between;align-items:flex-end;">
      <div>
        <div style="font-size:10px;letter-spacing:3px;color:#6b7f70;font-family:sans-serif;">DATE OF REGISTRATION</div>
        <div style="margin-top:6px;font-size:16px;color:#0f2a1a;">${date}</div>
      </div>
      <div style="text-align:center;">
        <div style="width:90px;height:90px;border-radius:50%;border:3px solid #b98a2b;display:flex;align-items:center;justify-content:center;flex-direction:column;box-shadow:0 0 0 3px #fdf9e7,0 0 0 4px #b98a2b;transform:rotate(-8deg);">
          <div style="font-size:9px;font-weight:700;color:#b98a2b;letter-spacing:1.5px;font-family:sans-serif;">SKYWAY GREEN</div>
          <div style="font-size:18px;color:#b98a2b;margin:2px 0;">&#9733;</div>
          <div style="font-size:7px;font-weight:700;color:#b98a2b;letter-spacing:2px;font-family:sans-serif;">COMMON SEAL</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:10px;letter-spacing:3px;color:#6b7f70;font-family:sans-serif;">AUTHORIZED ATTORNEY</div>
        <div style="margin-top:6px;font-size:22px;color:#0f2a1a;font-style:italic;font-family:'Great Vibes',cursive;">Evgenii Kudriashov</div>
        <div style="margin-top:4px;height:1px;background:#0f2a1a;width:160px;margin-left:auto;opacity:0.6;"></div>
        <div style="margin-top:4px;font-size:9px;letter-spacing:3px;color:#6b7f70;font-family:sans-serif;">SGW LEGAL REPRESENTATIVE</div>
      </div>
    </div>
  </div>`;

  const target = wrapper.querySelector('#cert-render');
  const canvas = await html2canvas(target, { scale: 2, backgroundColor: '#fdf9e7', useCORS: true });
  const imgData = canvas.toDataURL('image/png');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  // Preserve aspect ratio
  const imgRatio = canvas.width / canvas.height;
  let renderW = pageW - 40;
  let renderH = renderW / imgRatio;
  if (renderH > pageH - 40) {
    renderH = pageH - 40;
    renderW = renderH * imgRatio;
  }
  const x = (pageW - renderW) / 2;
  const y = (pageH - renderH) / 2;
  doc.addImage(imgData, 'PNG', x, y, renderW, renderH);
  doc.save(`SkywayGreen_Certificate_${(holderName || 'Investor').replace(/\s+/g, '_')}.pdf`);

  document.body.removeChild(wrapper);
};
